import React, { useState, useEffect, useMemo } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { SavedFiltersSidebar } from '@/components/SavedFiltersSidebar';
import { salaryAPI, organizationAPI, verticalAPI, exportAPI } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, formatDate, getMonthOptions, getYearOptions, getPreviousMonth, getPreviousMonthYear, getMonthName } from '@/lib/utils';
import { toast } from 'sonner';
import { EpfBadge } from '@/components/EpfBadge';
import {
  FileSpreadsheet,
  Loader2,
  Filter,
  Download,
  RefreshCw,
  CheckCircle,
  Clock,
  Banknote,
  Lock,
  Trash2,
  Plus,
  Users,
  Save,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Info,
  CreditCard,
  Landmark,
  User,
  Bookmark,
  Ban,
  AlertTriangle,
  ExternalLink,
  Eye,
} from 'lucide-react';
import ITEditForm from '@/components/salary/ITEditForm';
import RecoveryEditForm from '@/components/salary/RecoveryEditForm';

// Check if a salary record's month/year falls within the employee's notice period
function isInNoticePeriod(record) {
  if (!record.notice_start_date && !record.last_working_date) return false;
  if (record.employee_status !== 'on_notice') return false;

  const recVal = record.year * 12 + record.month;

  if (record.notice_start_date) {
    const ns = new Date(record.notice_start_date);
    const startVal = ns.getFullYear() * 12 + (ns.getMonth() + 1);
    if (recVal < startVal) return false;
  }

  if (record.last_working_date) {
    const lwd = new Date(record.last_working_date);
    const endVal = lwd.getFullYear() * 12 + (lwd.getMonth() + 1);
    if (recVal > endVal) return false;
  }

  return true;
}

export default function SalaryReportsPage({ lockedOrganizationId = null, lockedOrganizationName = null, excludedOrganizationId = null }) {
  const { canEditLOP, canFinalize, canMarkPaid, isAdmin, isAdminLevel, canViewSalary, isLOPManager, isSuperAdmin, canExportReports, canEditFinalized } = useAuth();
  const navigate = useNavigate();
  
  const [records, setRecords] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters - Default to previous month (now arrays for multi-select)
  // When `lockedOrganizationId` is set (e.g., Blogic module), the org filter is forced and not user-changeable.
  const [filterOrgs, setFilterOrgs] = useState(lockedOrganizationId ? [lockedOrganizationId] : []);
  const [filterVerticals, setFilterVerticals] = useState([]);
  const [filterMonth, setFilterMonth] = useState(getPreviousMonth().toString());
  const [filterYear, setFilterYear] = useState(getPreviousMonthYear().toString());
  const [filterPaid, setFilterPaid] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Sorting
  const [sortOrder, setSortOrder] = useState(null); // null, 'asc', 'desc'
  
  // Dialogs
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [bulkEntryDialogOpen, setBulkEntryDialogOpen] = useState(false);
  const [confirmFinalizeId, setConfirmFinalizeId] = useState(null);
  const [confirmUnfinalizeId, setConfirmUnfinalizeId] = useState(null);
  const [confirmPaidId, setConfirmPaidId] = useState(null);
  const [confirmReversePayId, setConfirmReversePayId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  
  // Bulk Entry State
  const [bulkOrgId, setBulkOrgId] = useState(lockedOrganizationId || '');
  const [bulkEmployees, setBulkEmployees] = useState([]);
  const [loadingBulk, setLoadingBulk] = useState(false);
  const [savingBulk, setSavingBulk] = useState(false);
  
  // Form state
  const [generating, setGenerating] = useState(false);
  
  // LOP, Reward, and Advance editing
  const [editingLOP, setEditingLOP] = useState({});
  const [editingReward, setEditingReward] = useState({});
  const [editingAdvance, setEditingAdvance] = useState({});
  const [editingIT, setEditingIT] = useState({});
  const [editingRecovery, setEditingRecovery] = useState({});
  
  // Saved Filters Sidebar
  const [savedFiltersOpen, setSavedFiltersOpen] = useState(false);

  // Filtered and sorted records
  const filteredAndSortedRecords = useMemo(() => {
    let filtered = records;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = records.filter(record => 
        record.employee_name?.toLowerCase().includes(query) ||
        record.employee_code?.toLowerCase().includes(query)
      );
    }
    
    // Apply sort by net payable
    if (sortOrder && canViewSalary()) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = a.net_payable || a.final_payable_salary || a.final_salary || 0;
        const bValue = b.net_payable || b.final_payable_salary || b.final_salary || 0;
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      });
    }
    
    return filtered;
  }, [records, searchQuery, sortOrder, canViewSalary]);

  const toggleSort = () => {
    if (sortOrder === null) setSortOrder('desc');
    else if (sortOrder === 'desc') setSortOrder('asc');
    else setSortOrder(null);
  };

  // Get current filters for saving
  const getCurrentFilters = () => ({
    month: filterMonth,
    year: filterYear,
    organizations: filterOrgs,
    verticals: filterVerticals,
    paid: filterPaid,
  });

  // Apply saved filter
  const handleApplyFilter = (filterConfig) => {
    if (filterConfig.month) setFilterMonth(filterConfig.month);
    if (filterConfig.year) setFilterYear(filterConfig.year);
    if (filterConfig.organizations) setFilterOrgs(filterConfig.organizations);
    if (filterConfig.verticals) setFilterVerticals(filterConfig.verticals);
    if (filterConfig.paid) setFilterPaid(filterConfig.paid);
  };

  // Filter labels for display in sidebar
  const filterLabels = {
    month: 'Month',
    year: 'Year',
    organizations: 'Organizations',
    verticals: 'Verticals',
    paid: 'Status',
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!loading) {
      loadRecords();
    }
  }, [filterOrgs, filterVerticals, filterMonth, filterYear, filterPaid]);

  const loadData = async () => {
    try {
      const [orgRes, vertRes] = await Promise.all([
        organizationAPI.getAll(),
        verticalAPI.getAll(),
      ]);
      let orgs = orgRes.data;
      // Hide locked org's siblings (Blogic module shows only Blogic)
      if (lockedOrganizationId) orgs = orgs.filter(o => o.id === lockedOrganizationId);
      // Default Salary Records hides Blogic from selectable orgs (it has its own dedicated module)
      if (excludedOrganizationId) orgs = orgs.filter(o => o.id !== excludedOrganizationId);
      setOrganizations(orgs);
      setVerticals(vertRes.data);
      await loadRecords();
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadRecords = async () => {
    try {
      const params = {
        month: parseInt(filterMonth),
        year: parseInt(filterYear),
      };
      // When locked, always force the org filter regardless of UI state
      if (lockedOrganizationId) {
        params.organization_ids = lockedOrganizationId;
      } else if (filterOrgs.length > 0) {
        params.organization_ids = filterOrgs.join(',');
      }
      // When excluded, instruct backend to skip records of that org
      if (excludedOrganizationId && !lockedOrganizationId) {
        params.exclude_organization_id = excludedOrganizationId;
      }
      if (filterVerticals.length > 0) params.vertical_ids = filterVerticals.join(',');
      if (filterPaid === 'paid') params.is_paid = true;
      if (filterPaid === 'unpaid') params.is_paid = false;

      const response = await salaryAPI.getAll(params);
      setRecords(response.data);
    } catch (error) {
      console.error('Failed to load records:', error);
    }
  };

  const handleGenerateMonthly = async () => {
    setGenerating(true);
    try {
      const response = await salaryAPI.generateMonthly(
        parseInt(filterMonth),
        parseInt(filterYear),
        filterOrgs.length === 1 ? filterOrgs[0] : null
      );
      toast.success(response.data.message);
      setGenerateDialogOpen(false);
      loadRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate records');
    } finally {
      setGenerating(false);
    }
  };

  // Bulk Entry Functions
  const handleOpenBulkEntry = () => {
    setBulkOrgId(lockedOrganizationId || '');
    setBulkEmployees([]);
    setBulkEntryDialogOpen(true);
  };

  const handleLoadBulkEmployees = async () => {
    if (!bulkOrgId) return;
    
    setLoadingBulk(true);
    try {
      const response = await salaryAPI.getByOrganization(
        bulkOrgId,
        parseInt(filterMonth),
        parseInt(filterYear)
      );
      setBulkEmployees(response.data.employees || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load employees');
    } finally {
      setLoadingBulk(false);
    }
  };

  const handleBulkLOPChange = (employeeId, value) => {
    setBulkEmployees(prev => prev.map(emp => {
      if (emp.employee_id === employeeId) {
        const lopDays = parseFloat(value) || 0;
        // Per Day = (Gross - Retention) / 30 (fixed divisor)
        const perDay = emp.net_eligible_salary / 30;
        const lopDeduction = perDay * lopDays;
        const finalPayable = emp.net_eligible_salary - lopDeduction;
        return { 
          ...emp, 
          lop_days: lopDays,
          per_day_salary: perDay,
          lop_deduction: lopDeduction,
          final_payable_salary: finalPayable
        };
      }
      return emp;
    }));
  };

  const handleSaveBulkEntry = async () => {
    setSavingBulk(true);
    try {
      const employeeLop = bulkEmployees.map(emp => ({
        employee_id: emp.employee_id,
        lop_days: emp.lop_days || 0
      }));

      await salaryAPI.bulkCreate({
        organization_id: bulkOrgId,
        month: parseInt(filterMonth),
        year: parseInt(filterYear),
        employee_lop: employeeLop
      });

      toast.success('Salary records saved successfully');
      setBulkEntryDialogOpen(false);
      loadRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save records');
    } finally {
      setSavingBulk(false);
    }
  };

  const handleLOPChange = (recordId, value) => {
    setEditingLOP({ ...editingLOP, [recordId]: value });
  };

  const handleRewardChange = (recordId, value) => {
    setEditingReward({ ...editingReward, [recordId]: value });
  };

  const handleLOPBlur = async (record) => {
    const newLOP = editingLOP[record.id];
    if (newLOP === undefined || newLOP === record.lop_days.toString()) {
      return;
    }
    
    try {
      await salaryAPI.updateLOP(record.id, { lop_days: parseFloat(newLOP) });
      toast.success('LOP Days updated');
      loadRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update LOP Days');
      setEditingLOP({ ...editingLOP, [record.id]: record.lop_days.toString() });
    }
  };

  const handleRewardBlur = async (record) => {
    const newReward = editingReward[record.id];
    if (newReward === undefined || newReward === (record.reward_amount || 0).toString()) {
      return;
    }
    
    try {
      await salaryAPI.updateLOP(record.id, { reward_amount: parseFloat(newReward) });
      toast.success('Reward updated');
      loadRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update reward');
      setEditingReward({ ...editingReward, [record.id]: (record.reward_amount || 0).toString() });
    }
  };

  const handleAdvanceChange = (recordId, value) => {
    setEditingAdvance({ ...editingAdvance, [recordId]: value });
  };

  const handleAdvanceBlur = async (record) => {
    const newAdvance = editingAdvance[record.id];
    const currentAdvance = record.advance_deduction_override ?? record.advance_deduction ?? 0;
    
    if (newAdvance === undefined || newAdvance === currentAdvance.toString()) {
      return;
    }
    
    try {
      await salaryAPI.updateLOP(record.id, { advance_deduction_override: parseFloat(newAdvance) });
      toast.success('Advance deduction updated');
      loadRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update advance deduction');
      setEditingAdvance({ ...editingAdvance, [record.id]: currentAdvance.toString() });
    }
  };

  const handleITSave = async (record, itType, itAmount, itPercent) => {
    try {
      const payload = { income_tax_type: itType || 'none' };
      if (itType === 'fixed') {
        payload.income_tax_amount = parseFloat(itAmount) || 0;
      } else if (itType === 'percent') {
        payload.income_tax_percent = parseFloat(itPercent) || 0;
      }
      await salaryAPI.updateLOP(record.id, payload);
      toast.success('Income tax updated');
      setEditingIT({});
      loadRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update income tax');
    }
  };

  const handleRecoverySave = async (record, amount, reason, comment) => {
    try {
      const amt = parseFloat(amount) || 0;
      await salaryAPI.updateLOP(record.id, {
        recovery_amount: amt,
        recovery_reason: amt > 0 ? (reason || null) : null,
        recovery_comment: amt > 0 ? (comment || null) : null,
      });
      toast.success(amt > 0 ? `Recovery of ₹${amt.toLocaleString('en-IN')} recorded` : 'Recovery cleared');
      setEditingRecovery({});
      loadRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update recovery');
    }
  };

  const handleFinalize = async (recordId) => {
    try {
      await salaryAPI.finalize(recordId);
      toast.success('Record finalized');
      setConfirmFinalizeId(null);
      loadRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to finalize');
    }
  };

  const handleUnfinalize = async (recordId) => {
    try {
      await salaryAPI.unfinalize(recordId);
      toast.success('Finalization reverted - record is now editable');
      setConfirmUnfinalizeId(null);
      loadRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revert finalization');
    }
  };

  const handleMarkPaid = async (recordId) => {
    try {
      await salaryAPI.markPaid(recordId);
      toast.success('Marked as paid');
      setConfirmPaidId(null);
      loadRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const handleReversePayment = async (recordId) => {
    try {
      await salaryAPI.reverse(recordId);
      toast.success('Payment reversed successfully');
      setConfirmReversePayId(null);
      loadRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reverse payment');
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await salaryAPI.delete(confirmDeleteId);
      toast.success('Record deleted');
      setConfirmDeleteId(null);
      loadRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleExportCSV = () => {
    const params = {
      month: filterMonth,
      year: filterYear,
    };
    if (lockedOrganizationId) params.organization_ids = lockedOrganizationId;
    else if (filterOrgs.length > 0) params.organization_ids = filterOrgs.join(',');
    if (excludedOrganizationId && !lockedOrganizationId) params.exclude_organization_id = excludedOrganizationId;
    if (filterVerticals.length > 0) params.vertical_ids = filterVerticals.join(',');
    if (filterPaid === 'paid') params.is_paid = 'true';
    if (filterPaid === 'unpaid') params.is_paid = 'false';

    const token = localStorage.getItem('paytrack_token');
    window.open(`${exportAPI.getCSVUrl(params)}&authorization=Bearer ${token}`, '_blank');
  };

  const handleExportExcel = () => {
    const params = {
      month: filterMonth,
      year: filterYear,
    };
    if (lockedOrganizationId) params.organization_ids = lockedOrganizationId;
    else if (filterOrgs.length > 0) params.organization_ids = filterOrgs.join(',');
    if (excludedOrganizationId && !lockedOrganizationId) params.exclude_organization_id = excludedOrganizationId;
    if (filterVerticals.length > 0) params.vertical_ids = filterVerticals.join(',');
    if (filterPaid === 'paid') params.is_paid = 'true';
    if (filterPaid === 'unpaid') params.is_paid = 'false';

    const token = localStorage.getItem('paytrack_token');
    window.open(`${exportAPI.getExcelUrl(params)}&authorization=Bearer ${token}`, '_blank');
  };

  const handleExportPDF = () => {
    const dateStr = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const monthName = getMonthName(parseInt(filterMonth));

    // Build table rows HTML
    let tableRowsHTML = filteredAndSortedRecords.map(record => {
      let status = 'Pending';
      let statusClass = 'status-pending';
      if (record.is_paid) {
        status = 'Paid';
        statusClass = 'status-paid';
      } else if (record.is_finalized) {
        status = 'Finalized';
        statusClass = 'status-finalized';
      }
      
      return `
        <tr>
          <td>${record.employee_code || ''}</td>
          <td>${record.employee_name || ''}</td>
          <td>${record.organization_name || ''}</td>
          <td>${record.vertical_name || ''}</td>
          <td class="right">${formatCurrency(record.gross_salary || 0)}</td>
          <td class="right">${formatCurrency(record.retention_amount || 0)}</td>
          <td class="right">${formatCurrency(record.net_eligible_salary || 0)}</td>
          <td class="right">${record.lop_days || 0}</td>
          <td class="right">${formatCurrency(record.lop_deduction || 0)}</td>
          <td class="right">${formatCurrency(record.reward_amount || 0)}</td>
          <td class="right">${formatCurrency(record.advance_deduction || 0)}</td>
          <td class="right"><strong>${formatCurrency(record.net_payable || 0)}</strong></td>
          <td><span class="${statusClass}">${status}</span></td>
        </tr>
      `;
    }).join('');

    // Calculate totals
    const totals = filteredAndSortedRecords.reduce((acc, r) => ({
      grossSalary: acc.grossSalary + (r.gross_salary || 0),
      retention: acc.retention + (r.retention_amount || 0),
      netEligible: acc.netEligible + (r.net_eligible_salary || 0),
      lopDeduction: acc.lopDeduction + (r.lop_deduction || 0),
      reward: acc.reward + (r.reward_amount || 0),
      advance: acc.advance + (r.advance_deduction || 0),
      netPayable: acc.netPayable + (r.net_payable || 0),
    }), { grossSalary: 0, retention: 0, netEligible: 0, lopDeduction: 0, reward: 0, advance: 0, netPayable: 0 });

    const filterInfo = [];
    if (filterOrgs.length > 0) filterInfo.push(`${filterOrgs.length} organization(s)`);
    if (filterVerticals.length > 0) filterInfo.push(`${filterVerticals.length} vertical(s)`);
    if (filterPaid !== 'all') filterInfo.push(`Status: ${filterPaid === 'paid' ? 'Paid' : 'Unpaid'}`);

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Salary Report - ${monthName} ${filterYear} - PayTrack</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, sans-serif; padding: 30px; color: #333; font-size: 11px; }
          .header { border-bottom: 2px solid #e5e7eb; padding-bottom: 15px; margin-bottom: 15px; }
          .header h1 { font-size: 20px; color: #111; }
          .header p { color: #666; font-size: 12px; margin-top: 4px; }
          .filters { background: #f9fafb; padding: 10px; border-radius: 6px; margin-bottom: 15px; font-size: 11px; }
          table { width: 100%; border-collapse: collapse; font-size: 10px; }
          th, td { padding: 6px 4px; text-align: left; border-bottom: 1px solid #e5e7eb; }
          th { background: #f3f4f6; font-weight: 600; color: #374151; white-space: nowrap; }
          .right { text-align: right; }
          .totals { background: #f0f9ff; font-weight: 600; }
          .status-paid { color: #059669; font-weight: 500; }
          .status-finalized { color: #7c3aed; font-weight: 500; }
          .status-pending { color: #d97706; font-weight: 500; }
          .footer { margin-top: 20px; padding-top: 15px; border-top: 1px solid #e5e7eb; font-size: 10px; color: #999; text-align: center; }
          @media print { 
            body { padding: 15px; }
            @page { size: landscape; margin: 10mm; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Salary Report - ${monthName} ${filterYear}</h1>
          <p>Generated on ${dateStr} • ${filteredAndSortedRecords.length} records</p>
        </div>
        ${filterInfo.length > 0 ? `<div class="filters">Filters: ${filterInfo.join(' | ')}</div>` : ''}
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Organization</th>
              <th>Vertical</th>
              <th class="right">Gross</th>
              <th class="right">Retention</th>
              <th class="right">Net Eligible</th>
              <th class="right">LOP Days</th>
              <th class="right">Absent Ded.</th>
              <th class="right">Reward</th>
              <th class="right">Advance Ded.</th>
              <th class="right">Net Payable</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${tableRowsHTML}
          </tbody>
          <tfoot>
            <tr class="totals">
              <td colspan="4"><strong>Total (${filteredAndSortedRecords.length} records)</strong></td>
              <td class="right">${formatCurrency(totals.grossSalary)}</td>
              <td class="right">${formatCurrency(totals.retention)}</td>
              <td class="right">${formatCurrency(totals.netEligible)}</td>
              <td class="right">-</td>
              <td class="right">${formatCurrency(totals.lopDeduction)}</td>
              <td class="right">${formatCurrency(totals.reward)}</td>
              <td class="right">${formatCurrency(totals.advance)}</td>
              <td class="right"><strong>${formatCurrency(totals.netPayable)}</strong></td>
              <td></td>
            </tr>
          </tfoot>
        </table>
        <div class="footer">PayTrack - Salary Reporting System</div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
    toast.success('PDF export ready for printing');
  };

  const getStatusBadge = (record) => {
    if (record.is_paid) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium status-paid border">
          <CheckCircle className="h-3 w-3" />
          Paid
        </span>
      );
    }
    if (record.is_finalized) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium status-finalized border">
          <Lock className="h-3 w-3" />
          Finalized
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium status-pending border">
        <Clock className="h-3 w-3" />
        Pending
      </span>
    );
  };

  const monthOptions = getMonthOptions();
  const yearOptions = getYearOptions();

  return (
    <DashboardLayout>
      <div data-testid="salary-reports-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {lockedOrganizationName ? `${lockedOrganizationName} — Salary Entry` : (isLOPManager() ? 'LOP Days' : 'Salary Reports')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {getMonthName(parseInt(filterMonth))} {filterYear} - {records.length} records{lockedOrganizationName ? ' · dedicated module' : ''}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="default"
              onClick={handleOpenBulkEntry}
              data-testid="bulk-entry-btn"
            >
              <Users className="h-4 w-4 mr-2" />
              Bulk Entry
            </Button>
            {isAdminLevel() && (
              <Button
                variant="outline"
                onClick={() => setGenerateDialogOpen(true)}
                data-testid="generate-monthly-btn"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Generate Monthly
              </Button>
            )}
            {canExportReports() && (
              <>
                <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
                  <Download className="h-4 w-4 mr-2" />
                  CSV
                </Button>
                <Button variant="outline" onClick={handleExportExcel} data-testid="export-excel-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Excel
                </Button>
                <Button variant="outline" onClick={handleExportPDF} data-testid="export-pdf-btn">
                  <Download className="h-4 w-4 mr-2" />
                  PDF
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 p-4 bg-muted/30 rounded-lg">
          {/* Search Input */}
          <div className="relative flex-grow min-w-[200px] max-w-[300px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search employee name or code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-employee-input"
            />
          </div>

          <Select value={filterMonth} onValueChange={setFilterMonth}>
            <SelectTrigger className="w-[140px]" data-testid="filter-month-select">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((m) => (
                <SelectItem key={m.value} value={m.value.toString()}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterYear} onValueChange={setFilterYear}>
            <SelectTrigger className="w-[100px]" data-testid="filter-year-select">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => (
                <SelectItem key={y} value={y.toString()}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {!lockedOrganizationId && (
            <div className="w-[200px]">
              <MultiSelect
                options={organizations.map(org => ({ value: org.id, label: org.name }))}
                selected={filterOrgs}
                onChange={setFilterOrgs}
                placeholder="All Organizations"
                data-testid="filter-org-select"
              />
            </div>
          )}

          <div className="w-[160px]">
            <MultiSelect
              options={verticals.map(v => ({ value: v.id, label: v.name }))}
              selected={filterVerticals}
              onChange={setFilterVerticals}
              placeholder="All Verticals"
              data-testid="filter-vertical-select"
            />
          </div>

          <Select value={filterPaid} onValueChange={setFilterPaid}>
            <SelectTrigger className="w-[130px]" data-testid="filter-paid-select">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="unpaid">Unpaid</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="default"
            onClick={() => setSavedFiltersOpen(true)}
            data-testid="open-saved-filters-btn"
          >
            <Bookmark className="h-4 w-4 mr-2" />
            Saved Filters
          </Button>
        </div>

        {/* Saved Filters Sidebar */}
        <SavedFiltersSidebar
          isOpen={savedFiltersOpen}
          onClose={() => setSavedFiltersOpen(false)}
          page="salary_reports"
          currentFilters={getCurrentFilters()}
          onApplyFilter={handleApplyFilter}
          filterLabels={filterLabels}
        />

        {/* Notice Period Summary Strip */}
        {(() => {
          const noticeRecords = filteredAndSortedRecords.filter(isInNoticePeriod);
          if (noticeRecords.length === 0) return null;
          const frozenSalary = noticeRecords.reduce((sum, r) => sum + (r.final_payable_salary || 0), 0);
          const pendingSettlements = noticeRecords.filter(r => !r.is_finalized).length;
          return (
            <div className="flex items-center gap-6 px-4 py-2.5 rounded-lg border border-orange-200 bg-orange-50/60 dark:border-orange-800 dark:bg-orange-950/20" data-testid="notice-period-summary">
              <div className="flex items-center gap-2 text-orange-700 dark:text-orange-400">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span className="text-sm font-semibold">Notice Period</span>
              </div>
              <div className="flex items-center gap-1 text-sm">
                <span className="text-muted-foreground">On Notice:</span>
                <span className="font-bold text-orange-700 dark:text-orange-400">{noticeRecords.length}</span>
              </div>
              <div className="flex items-center gap-1 text-sm">
                <span className="text-muted-foreground">Frozen Salary:</span>
                <span className="font-bold font-mono text-orange-700 dark:text-orange-400">{formatCurrency(frozenSalary)}</span>
              </div>
              <div className="flex items-center gap-1 text-sm">
                <span className="text-muted-foreground">Pending Settlements:</span>
                <span className="font-bold text-orange-700 dark:text-orange-400">{pendingSettlements}</span>
              </div>
              <Button variant="ghost" size="sm" className="ml-auto text-orange-700 hover:text-orange-800 hover:bg-orange-100 dark:text-orange-400 dark:hover:bg-orange-950/40 h-7 text-xs" onClick={() => navigate('/resigned-employees')} data-testid="go-to-settlements-btn">
                View Settlements <ExternalLink className="h-3 w-3 ml-1" />
              </Button>
            </div>
          );
        })()}

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 spinner text-muted-foreground" />
              </div>
            ) : records.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <FileSpreadsheet className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">
                  No salary records for {getMonthName(parseInt(filterMonth))} {filterYear}
                </p>
                <Button
                  variant="link"
                  onClick={handleOpenBulkEntry}
                  className="mt-2"
                >
                  Add salary records using Bulk Entry
                </Button>
              </div>
            ) : (
              <div className="overflow-auto" style={{ maxHeight: '600px' }}>
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead className="min-w-[150px] sticky left-0 bg-background z-20">Employee</TableHead>
                      <TableHead className="min-w-[120px]">Organization</TableHead>
                      {canViewSalary() && (
                        <>
                          <TableHead className="text-right min-w-[100px]">Gross</TableHead>
                          <TableHead className="text-right min-w-[90px]">Retention</TableHead>
                          <TableHead className="text-right min-w-[100px]">Net Eligible</TableHead>
                          <TableHead className="text-right min-w-[80px]">Per Day</TableHead>
                        </>
                      )}
                      <TableHead className="text-center min-w-[70px]">LOP Days</TableHead>
                      {canViewSalary() && (
                        <>
                          <TableHead className="text-right min-w-[90px]">Absent Ded.</TableHead>
                          {lockedOrganizationId && (
                            <>
                              <TableHead className="text-right min-w-[80px]">Emp PF</TableHead>
                              <TableHead className="text-right min-w-[80px]">Empr PF</TableHead>
                            </>
                          )}
                          <TableHead className="text-center min-w-[80px]">Reward</TableHead>
                          <TableHead className="text-right min-w-[110px]">Final Payable</TableHead>
                          <TableHead className="text-right min-w-[120px]">
                            <div className="flex items-center justify-end gap-1">
                              Advance Ded.
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <Info className="h-3.5 w-3.5 text-muted-foreground" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Click on amount to see breakdown
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </TableHead>
                          <TableHead 
                            className="text-right min-w-[120px] cursor-pointer hover:bg-muted/50 select-none"
                            onClick={toggleSort}
                            data-testid="sort-net-payable"
                          >
                            <div className="flex items-center justify-end gap-1">
                              Net Payable
                              {sortOrder === null && <ArrowUpDown className="h-4 w-4 text-muted-foreground" />}
                              {sortOrder === 'asc' && <ArrowUp className="h-4 w-4 text-primary" />}
                              {sortOrder === 'desc' && <ArrowDown className="h-4 w-4 text-primary" />}
                            </div>
                          </TableHead>
                          <TableHead className="text-right min-w-[120px] bg-indigo-50/50 border-l border-indigo-100">
                            <span className="text-indigo-700">Income Tax</span>
                          </TableHead>
                          <TableHead className="text-right min-w-[140px] bg-rose-50/40 border-l border-rose-100">
                            <span className="text-rose-700">Recovery</span>
                          </TableHead>
                          <TableHead className="text-right min-w-[120px]">
                            <span className="text-emerald-700">Bank Transfer</span>
                          </TableHead>
                        </>
                      )}
                      <TableHead className="text-center min-w-[90px]">Status</TableHead>
                      {canViewSalary() && (
                        <>
                          <TableHead className="text-center min-w-[90px]">Finalized</TableHead>
                          <TableHead className="text-center min-w-[90px]">Paid</TableHead>
                        </>
                      )}
                      <TableHead className="text-right min-w-[60px] sticky right-0 bg-background z-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAndSortedRecords.map((record) => {
                      const grossSalary = record.gross_salary || record.monthly_salary || 0;
                      const retention = record.retention_amount || record.retention_bonus || 0;
                      const netEligible = record.net_eligible_salary || (grossSalary - retention);
                      const perDaySalary = netEligible / 30;
                      const lopDeduction = record.lop_deduction || record.deduction || 0;
                      const rewardAmount = record.reward_amount || 0;
                      const finalPayable = record.final_payable_salary || record.final_salary || 0;
                      const advanceDeduction = record.advance_deduction || 0;
                      const advanceDetails = record.advance_deduction_details || [];
                      const netPayable = record.net_payable || finalPayable;
                      
                      // Super Admin can edit even finalized records
                      const canEditThisRecord = canEditFinalized() || (!record.is_finalized && canEditLOP());
                      
                      return (
                        <TableRow
                          key={record.id}
                          className={`table-row-hover ${record.is_paid ? 'bg-emerald-50/30 dark:bg-emerald-950/20' : ''} ${isInNoticePeriod(record) ? 'bg-orange-50/40 dark:bg-orange-950/15' : ''}`}
                          data-testid={`salary-row-${record.id}`}
                        >
                          <TableCell className="sticky left-0 bg-background">
                            <div className="flex items-center gap-2">
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <p className="font-medium">{record.employee_name}</p>
                                  {isInNoticePeriod(record) && (
                                    <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-bold bg-orange-500 text-white">NOTICE</span>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground font-mono">{record.employee_code}</p>
                              </div>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-6 w-6"
                                    data-testid={`bank-icon-${record.id}`}
                                  >
                                    <Landmark className={`h-3.5 w-3.5 ${
                                      record.bank_details?.account_number || record.bank_details?.bank_name 
                                        ? 'text-blue-500' 
                                        : 'text-muted-foreground/50'
                                    }`} />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-72" align="start">
                                  <div className="space-y-3">
                                    <div className="flex items-center gap-2 border-b pb-2">
                                      <User className="h-4 w-4 text-muted-foreground" />
                                      <div>
                                        <p className="font-semibold">{record.employee_name}</p>
                                        <p className="text-xs text-muted-foreground">{record.employee_email || record.employee_code}</p>
                                      </div>
                                    </div>
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2">
                                        <Landmark className={`h-4 w-4 ${
                                          record.bank_details?.account_number || record.bank_details?.bank_name 
                                            ? 'text-blue-500' 
                                            : 'text-muted-foreground'
                                        }`} />
                                        <span className="font-medium text-sm">Bank Details</span>
                                      </div>
                                      <div className="grid gap-1.5 text-sm pl-6">
                                        {record.bank_details?.bank_name ? (
                                          <div className="flex justify-between">
                                            <span className="text-muted-foreground">Bank</span>
                                            <span className="font-medium">{record.bank_details.bank_name}</span>
                                          </div>
                                        ) : null}
                                        {record.bank_details?.account_holder_name ? (
                                          <div className="flex justify-between">
                                            <span className="text-muted-foreground">Holder</span>
                                            <span className="font-medium">{record.bank_details.account_holder_name}</span>
                                          </div>
                                        ) : null}
                                        {record.bank_details?.account_number ? (
                                          <div className="flex justify-between">
                                            <span className="text-muted-foreground">Account</span>
                                            <span className="font-mono text-xs">{record.bank_details.account_number}</span>
                                          </div>
                                        ) : null}
                                        {record.bank_details?.ifsc_code ? (
                                          <div className="flex justify-between">
                                            <span className="text-muted-foreground">IFSC</span>
                                            <span className="font-mono text-xs">{record.bank_details.ifsc_code}</span>
                                          </div>
                                        ) : null}
                                        {(!record.bank_details?.account_number && !record.bank_details?.bank_name) && (
                                          <div className="py-2 text-center">
                                            <p className="text-muted-foreground italic text-sm">No bank details available</p>
                                            <p className="text-xs text-muted-foreground/70 mt-1">Add bank details in the Employee page</p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            <span className="inline-flex items-center gap-1.5">
                              {record.organization_name}
                              <EpfBadge enabled={organizations.find(o => o.id === record.organization_id)?.epf_enabled} />
                            </span>
                          </TableCell>
                          {canViewSalary() && (
                            <>
                              <TableCell className="text-right font-mono">
                                {formatCurrency(grossSalary)}
                              </TableCell>
                              <TableCell className="text-right font-mono text-amber-600 dark:text-amber-400">
                                {retention > 0 ? formatCurrency(retention) : '-'}
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {formatCurrency(netEligible)}
                              </TableCell>
                              <TableCell className="text-right font-mono text-xs text-muted-foreground">
                                {formatCurrency(perDaySalary)}
                              </TableCell>
                            </>
                          )}
                          <TableCell className="text-center">
                            {canEditThisRecord ? (
                              <div>
                                <Input
                                  type="number"
                                  value={editingLOP[record.id] ?? record.lop_days}
                                  onChange={(e) => handleLOPChange(record.id, e.target.value)}
                                  onBlur={() => handleLOPBlur(record)}
                                  className="w-16 text-center mx-auto font-mono"
                                  min="0"
                                  max="31"
                                  step="0.5"
                                  data-testid={`lop-input-${record.id}`}
                                />
                                {record.keka_matched && <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-300">Keka</span>}
                              </div>
                            ) : (
                              <div>
                                <span className="font-mono">{record.lop_days}</span>
                                {record.keka_matched && <span className="inline-block ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-300">Keka</span>}
                              </div>
                            )}
                          </TableCell>
                          {canViewSalary() && (
                            <>
                              <TableCell className={`text-right font-mono ${lopDeduction > 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                                {lopDeduction > 0 ? `-${formatCurrency(lopDeduction)}` : '-'}
                              </TableCell>
                              {lockedOrganizationId && (
                                <>
                                  <TableCell className={`text-right font-mono ${(record.epf_employee || record.epf_deduction || 0) > 0 ? 'text-orange-600 dark:text-orange-400' : ''}`}>
                                    {(record.epf_employee || record.epf_deduction || 0) > 0 ? `-${formatCurrency(record.epf_employee || record.epf_deduction)}` : '-'}
                                  </TableCell>
                                  <TableCell className={`text-right font-mono ${(record.epf_employer || 0) > 0 ? 'text-blue-600 dark:text-blue-400' : ''}`}>
                                    {(record.epf_employer || 0) > 0 ? formatCurrency(record.epf_employer) : '-'}
                                  </TableCell>
                                </>
                              )}
                              <TableCell className="text-center">
                                {canEditThisRecord ? (
                                  <Input
                                    type="number"
                                    value={editingReward[record.id] ?? rewardAmount}
                                    onChange={(e) => handleRewardChange(record.id, e.target.value)}
                                    onBlur={() => handleRewardBlur(record)}
                                    className="w-20 text-center mx-auto font-mono"
                                    min="0"
                                    step="100"
                                    data-testid={`reward-input-${record.id}`}
                                  />
                                ) : (
                                  <span className={`font-mono ${rewardAmount > 0 ? 'text-violet-600 dark:text-violet-400' : ''}`}>
                                    {rewardAmount > 0 ? `+${formatCurrency(rewardAmount)}` : '-'}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {formatCurrency(finalPayable)}
                              </TableCell>
                              {/* Advance Deduction - Editable */}
                              <TableCell className="text-right">
                                {canEditThisRecord ? (
                                  <div className="flex items-center justify-end gap-1">
                                    <Input
                                      type="number"
                                      value={editingAdvance[record.id] ?? advanceDeduction}
                                      onChange={(e) => handleAdvanceChange(record.id, e.target.value)}
                                      onBlur={() => handleAdvanceBlur(record)}
                                      className="w-24 text-right font-mono text-orange-600"
                                      min="0"
                                      step="100"
                                      data-testid={`advance-input-${record.id}`}
                                    />
                                    {advanceDetails.length > 0 && (
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <Button variant="ghost" size="icon" className="h-8 w-8">
                                            <Info className="h-4 w-4 text-muted-foreground" />
                                          </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-80" align="end">
                                          <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                              <CreditCard className="h-4 w-4 text-orange-500" />
                                              <h4 className="font-semibold">Advance Deduction Details</h4>
                                            </div>
                                            <div className="space-y-2">
                                              {advanceDetails.map((detail, idx) => (
                                                <div key={idx} className="flex justify-between items-start text-sm border-b pb-2 last:border-0">
                                                  <div>
                                                    <p className="font-medium">{detail.advance_name}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                      {detail.advance_type === 'one_time' ? 'One-time' : detail.advance_type === 'override' ? 'Manual Override' : 'Installment'}
                                                    </p>
                                                  </div>
                                                  <div className="text-right">
                                                    <p className="font-mono text-orange-600">-{formatCurrency(detail.deducted_amount)}</p>
                                                    {detail.remaining_balance !== undefined && (
                                                      <p className="text-xs text-muted-foreground">
                                                        Balance: {formatCurrency(detail.remaining_balance)}
                                                      </p>
                                                    )}
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                              Edit the input field to override auto-calculated amount
                                            </p>
                                          </div>
                                        </PopoverContent>
                                      </Popover>
                                    )}
                                  </div>
                                ) : advanceDeduction > 0 ? (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-auto py-1 px-2 font-mono text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-950">
                                        -{formatCurrency(advanceDeduction)}
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-80" align="end">
                                      <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                          <CreditCard className="h-4 w-4 text-orange-500" />
                                          <h4 className="font-semibold">Advance Deduction Breakdown</h4>
                                        </div>
                                        <div className="space-y-2">
                                          {advanceDetails.map((detail, idx) => (
                                            <div key={idx} className="flex justify-between items-start text-sm border-b pb-2 last:border-0">
                                              <div>
                                                <p className="font-medium">{detail.advance_name}</p>
                                                <p className="text-xs text-muted-foreground">
                                                  {detail.advance_type === 'one_time' ? 'One-time' : detail.advance_type === 'override' ? 'Manual Override' : 'Installment'}
                                                </p>
                                              </div>
                                              <div className="text-right">
                                                <p className="font-mono text-orange-600">-{formatCurrency(detail.deducted_amount)}</p>
                                                {detail.remaining_balance !== undefined && (
                                                  <p className="text-xs text-muted-foreground">
                                                    Balance: {formatCurrency(detail.remaining_balance)}
                                                  </p>
                                                )}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                        <div className="pt-2 border-t flex justify-between font-semibold">
                                          <span>Total Deduction</span>
                                          <span className="font-mono text-orange-600">-{formatCurrency(advanceDeduction)}</span>
                                        </div>
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                ) : (
                                  <span className="font-mono text-muted-foreground">-</span>
                                )}
                              </TableCell>
                              {/* Net Payable */}
                              <TableCell className="text-right font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                                {formatCurrency(netPayable)}
                              </TableCell>
                              {/* Income Tax */}
                              <TableCell className="text-right bg-indigo-50/30 border-l border-indigo-100">
                                {canEditThisRecord ? (
                                  <Popover open={editingIT[record.id]} onOpenChange={(open) => setEditingIT({...editingIT, [record.id]: open})}>
                                    <PopoverTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-auto py-1 px-2 font-mono text-indigo-600 hover:bg-indigo-100" data-testid={`it-edit-${record.id}`}>
                                        {record.income_tax_type ? (
                                          <>
                                            -{formatCurrency(record.income_tax_amount || 0)}
                                            {record.income_tax_type === 'percent' && <span className="text-xs ml-1">({record.income_tax_percent}%)</span>}
                                          </>
                                        ) : (
                                          <span className="text-muted-foreground text-xs">Set IT</span>
                                        )}
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-72" align="end">
                                      <ITEditForm
                                        record={record}
                                        onSave={(type, amount, pct) => { handleITSave(record, type, amount, pct); }}
                                      />
                                    </PopoverContent>
                                  </Popover>
                                ) : record.income_tax_type ? (
                                  <span className="font-mono text-indigo-700">
                                    -{formatCurrency(record.income_tax_amount || 0)}
                                    {record.income_tax_type === 'percent' && <span className="text-xs ml-1">({record.income_tax_percent}%)</span>}
                                  </span>
                                ) : (
                                  <span className="text-xs text-muted-foreground">-</span>
                                )}
                              </TableCell>
                              {/* Recovery */}
                              <TableCell className="text-right bg-rose-50/20 border-l border-rose-100">
                                {canEditThisRecord ? (
                                  <Popover open={editingRecovery[record.id]} onOpenChange={(open) => setEditingRecovery({...editingRecovery, [record.id]: open})}>
                                    <PopoverTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-auto py-1 px-2 font-mono text-rose-600 hover:bg-rose-100" data-testid={`recovery-edit-${record.id}`}>
                                        {(record.recovery_amount || 0) > 0 ? (
                                          <div className="flex flex-col items-end leading-tight">
                                            <span>-{formatCurrency(record.recovery_amount)}</span>
                                            {record.recovery_reason && (
                                              <span className="text-[10px] text-rose-500 normal-case">{record.recovery_reason}</span>
                                            )}
                                          </div>
                                        ) : (
                                          <span className="text-muted-foreground text-xs">Add</span>
                                        )}
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-72" align="end">
                                      <RecoveryEditForm
                                        record={record}
                                        onSave={(amount, reason, comment) => handleRecoverySave(record, amount, reason, comment)}
                                      />
                                    </PopoverContent>
                                  </Popover>
                                ) : (record.recovery_amount || 0) > 0 ? (
                                  <span className="font-mono text-rose-700">
                                    -{formatCurrency(record.recovery_amount)}
                                    {record.recovery_reason && <span className="text-[10px] ml-1">({record.recovery_reason})</span>}
                                  </span>
                                ) : (
                                  <span className="text-xs text-muted-foreground">-</span>
                                )}
                              </TableCell>
                              {/* Bank Transfer */}
                              <TableCell className="text-right font-mono font-semibold text-emerald-700">
                                {formatCurrency(record.bank_transfer || netPayable)}
                              </TableCell>
                            </>
                          )}
                          <TableCell className="text-center">
                            {getStatusBadge(record)}
                          </TableCell>
                          {canViewSalary() && (
                            <>
                              <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Checkbox
                                checked={record.is_finalized}
                                disabled={(!record.is_finalized && !canFinalize()) || (record.is_finalized && !canEditFinalized())}
                                onCheckedChange={() => {
                                  if (record.is_finalized) {
                                    setConfirmUnfinalizeId(record.id);
                                  } else {
                                    setConfirmFinalizeId(record.id);
                                  }
                                }}
                                data-testid={`finalize-checkbox-${record.id}`}
                              />
                              {/* Eye icon — full breakdown for cross-checking the math */}
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-5 w-5" data-testid={`finalize-breakdown-${record.id}`} title="View calculation breakdown">
                                    <Eye className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-80" align="center">
                                  {(() => {
                                    const gross = record.gross_salary || 0;
                                    const retention = record.retention_amount || 0;
                                    const netEligible = record.net_eligible_salary || (gross - retention);
                                    const lopDays = record.lop_days || 0;
                                    const perDay = record.per_day_salary || 0;
                                    const lopDed = record.lop_deduction || (lopDays * perDay);
                                    const reward = record.reward_amount || 0;
                                    const empPF = record.epf_employee || record.epf_deduction || 0;
                                    const finalPayable = record.final_payable_salary || 0;
                                    const advDed = record.advance_deduction || 0;
                                    const netPayable = record.net_payable || (finalPayable - advDed);
                                    const itAmount = record.income_tax_amount || 0;
                                    const bankTransfer = record.bank_transfer || (netPayable - itAmount);
                                    const Row = ({ label, value, neg, bold, total, sub }) => (
                                      <div className={`flex justify-between ${bold ? 'font-semibold' : ''} ${total ? 'border-t pt-1.5 mt-1.5 text-base font-bold text-emerald-600' : ''} ${sub ? 'text-xs text-muted-foreground' : ''}`}>
                                        <span>{label}</span>
                                        <span className={`font-mono ${neg ? 'text-red-600' : ''}`}>
                                          {neg ? '-' : ''}{formatCurrency(Math.abs(value))}
                                        </span>
                                      </div>
                                    );
                                    return (
                                      <div className="space-y-2 text-sm">
                                        <div className="font-semibold text-sm flex items-center justify-between border-b pb-1.5 mb-1">
                                          <span>Calculation Breakdown</span>
                                          {record.is_finalized && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">FINALIZED</span>
                                          )}
                                        </div>
                                        <Row label="Gross Salary" value={gross} bold />
                                        <Row label="Retention Held" value={retention} neg />
                                        <Row label="Net Eligible" value={netEligible} bold />
                                        {lopDays > 0 && <Row label={`LOP (${lopDays} day${lopDays === 1 ? '' : 's'} × ${formatCurrency(perDay)})`} value={lopDed} neg sub />}
                                        {reward > 0 && <Row label="Reward" value={reward} sub />}
                                        {empPF > 0 && <Row label="Employee PF (12% of Basic)" value={empPF} neg sub />}
                                        <Row label="Final Payable" value={finalPayable} bold />
                                        {advDed > 0 && <Row label="Advance Deductions" value={advDed} neg sub />}
                                        <Row label="Net Payable" value={netPayable} bold />
                                        {itAmount > 0 && <Row label="Income Tax (TDS)" value={itAmount} neg sub />}
                                        <Row label="Total Bank Transfer" value={bankTransfer} total />
                                      </div>
                                    );
                                  })()}
                                </PopoverContent>
                              </Popover>
                              {record.is_finalized && record.finalized_at && (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-5 w-5">
                                      <Info className="h-3 w-3 text-muted-foreground" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-64" align="center">
                                    <div className="space-y-3 text-sm">
                                      <div className="flex items-center gap-2 text-emerald-600">
                                        <CheckCircle className="h-4 w-4" />
                                        <span className="font-medium">Finalized</span>
                                      </div>
                                      <div className="pl-6 space-y-1">
                                        <div className="flex justify-between">
                                          <span className="text-muted-foreground">Date</span>
                                          <span>{formatDate(record.finalized_at)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-muted-foreground">By</span>
                                          <span className="font-medium">{record.finalized_by_name || 'Unknown'}</span>
                                        </div>
                                      </div>
                                      {/* Unfinalize option - Super Admin can always see it */}
                                      {isAdminLevel() && (!record.is_paid || canEditFinalized()) && (
                                        <div className="pt-2 border-t">
                                          {record.is_paid && (
                                            <p className="text-xs text-amber-600 mb-2">Reverse payment first to unfinalize</p>
                                          )}
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="w-full text-amber-600 border-amber-300 hover:bg-amber-50"
                                            onClick={() => setConfirmUnfinalizeId(record.id)}
                                            data-testid={`unfinalize-btn-${record.id}`}
                                          >
                                            <RefreshCw className="h-3 w-3 mr-1" />
                                            Revert Finalization
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              {isInNoticePeriod(record) ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold bg-orange-100 text-orange-700 border border-orange-300 dark:bg-orange-950 dark:text-orange-300" data-testid={`do-not-pay-${record.id}`} title="Settlement via Resigned Employees page">
                                  <Ban className="h-3 w-3" />
                                  DO NOT PAY
                                </span>
                              ) : (
                              <Checkbox
                                checked={record.is_paid}
                                disabled={(!record.is_paid && (!record.is_finalized || !canMarkPaid())) || (record.is_paid && !canEditFinalized())}
                                onCheckedChange={() => {
                                  if (record.is_paid) {
                                    setConfirmReversePayId(record.id);
                                  } else {
                                    setConfirmPaidId(record.id);
                                  }
                                }}
                                data-testid={`paid-checkbox-${record.id}`}
                              />
                              )}
                              {record.is_paid && record.paid_at && (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-5 w-5">
                                      <Info className="h-3 w-3 text-muted-foreground" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-56" align="center">
                                    <div className="space-y-2 text-sm">
                                      <div className="flex items-center gap-2 text-emerald-600">
                                        <Banknote className="h-4 w-4" />
                                        <span className="font-medium">Payment Done</span>
                                      </div>
                                      <div className="pl-6 space-y-1">
                                        <div className="flex justify-between">
                                          <span className="text-muted-foreground">Date</span>
                                          <span>{formatDate(record.paid_at)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-muted-foreground">By</span>
                                          <span className="font-medium">{record.paid_by_name || 'Unknown'}</span>
                                        </div>
                                      </div>
                                      {/* Reverse Payment option for Super Admin */}
                                      {canEditFinalized() && (
                                        <div className="pt-2 border-t">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="w-full text-red-600 border-red-300 hover:bg-red-50"
                                            onClick={() => setConfirmReversePayId(record.id)}
                                            data-testid={`reverse-payment-btn-${record.id}`}
                                          >
                                            <RefreshCw className="h-3 w-3 mr-1" />
                                            Reverse Payment
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              )}
                            </div>
                          </TableCell>
                            </>
                          )}
                          <TableCell className="text-right sticky right-0 bg-background">
                            {isAdminLevel() && (!record.is_paid || canEditFinalized()) && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setConfirmDeleteId(record.id)}
                                className="text-destructive hover:text-destructive"
                                data-testid={`delete-record-${record.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary */}
        {filteredAndSortedRecords.length > 0 && canViewSalary() && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card className="bg-muted/30">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Final Payable</p>
                <p className="text-xl font-bold font-mono">
                  {formatCurrency(filteredAndSortedRecords.reduce((sum, r) => sum + (r.final_payable_salary || r.final_salary || 0), 0))}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-muted/30">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Advance Deductions</p>
                <p className="text-xl font-bold font-mono text-orange-600 dark:text-orange-400">
                  {formatCurrency(filteredAndSortedRecords.reduce((sum, r) => sum + (r.advance_deduction || 0), 0))}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Net Payable</p>
                <p className="text-xl font-bold font-mono text-emerald-600 dark:text-emerald-400">
                  {formatCurrency(filteredAndSortedRecords.reduce((sum, r) => sum + (r.net_payable || r.final_payable_salary || 0), 0))}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-indigo-50 dark:bg-indigo-950/30 border-indigo-200 dark:border-indigo-800">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Income Tax</p>
                <p className="text-xl font-bold font-mono text-indigo-600 dark:text-indigo-400">
                  {formatCurrency(filteredAndSortedRecords.reduce((sum, r) => sum + (r.income_tax_amount || 0), 0))}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Recovery</p>
                <p className="text-xl font-bold font-mono text-rose-600 dark:text-rose-400">
                  {formatCurrency(filteredAndSortedRecords.reduce((sum, r) => sum + (r.recovery_amount || 0), 0))}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Bank Transfer</p>
                <p className="text-xl font-bold font-mono text-emerald-700 dark:text-emerald-300">
                  {formatCurrency(filteredAndSortedRecords.reduce((sum, r) => sum + (r.bank_transfer || r.net_payable || r.final_payable_salary || 0), 0))}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-muted/30">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Absent Deductions</p>
                <p className="text-xl font-bold font-mono text-red-600 dark:text-red-400">
                  {formatCurrency(filteredAndSortedRecords.reduce((sum, r) => sum + (r.lop_deduction || r.deduction || 0), 0))}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-muted/30">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Retention Held</p>
                <p className="text-xl font-bold font-mono text-amber-600 dark:text-amber-400">
                  {formatCurrency(filteredAndSortedRecords.reduce((sum, r) => sum + (r.retention_amount || r.retention_bonus || 0), 0))}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-muted/30">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Pending Payments</p>
                <p className="text-2xl font-bold font-mono">
                  {filteredAndSortedRecords.filter(r => !r.is_paid).length} / {filteredAndSortedRecords.length}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* LOP Manager Summary - only show record count */}
        {filteredAndSortedRecords.length > 0 && !canViewSalary() && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="bg-muted/30">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Total Records</p>
                <p className="text-2xl font-bold font-mono">
                  {filteredAndSortedRecords.length}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-muted/30">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Total LOP Days</p>
                <p className="text-2xl font-bold font-mono">
                  {filteredAndSortedRecords.reduce((sum, r) => sum + (r.lop_days || 0), 0)}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Bulk Entry Dialog */}
        <Dialog open={bulkEntryDialogOpen} onOpenChange={setBulkEntryDialogOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Bulk Salary Entry - {getMonthName(parseInt(filterMonth))} {filterYear}
              </DialogTitle>
              <DialogDescription>
                Select a company to enter salary details for all employees at once
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
              {/* Organization Selection */}
              <div className="flex gap-4">
                <Select value={bulkOrgId} onValueChange={setBulkOrgId} disabled={!!lockedOrganizationId}>
                  <SelectTrigger className="w-[300px]" data-testid="bulk-org-select">
                    <SelectValue placeholder="Select Company" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  onClick={handleLoadBulkEmployees} 
                  disabled={!bulkOrgId || loadingBulk}
                  data-testid="load-employees-btn"
                >
                  {loadingBulk ? <Loader2 className="h-4 w-4 spinner mr-2" /> : null}
                  Load Employees
                </Button>
              </div>

              {/* Employees Table */}
              {bulkEmployees.length > 0 && (
                <div className="flex-1 overflow-auto border rounded-lg">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background">
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Vertical</TableHead>
                        <TableHead className="text-right">Gross</TableHead>
                        <TableHead className="text-right">Retention</TableHead>
                        <TableHead className="text-right">Net Eligible</TableHead>
                        <TableHead className="text-right">Per Day</TableHead>
                        <TableHead className="text-center w-24">LOP Days</TableHead>
                        <TableHead className="text-right">Absent Deduction</TableHead>
                        <TableHead className="text-right">Final Payable</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bulkEmployees.map((emp) => (
                        <TableRow key={emp.employee_id} data-testid={`bulk-row-${emp.employee_id}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{emp.name}</p>
                              <p className="text-xs text-muted-foreground font-mono">{emp.employee_code}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">{emp.vertical_name}</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(emp.gross_salary)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-amber-600">
                            {emp.retention_amount > 0 ? formatCurrency(emp.retention_amount) : '-'}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(emp.net_eligible_salary)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs text-muted-foreground">
                            {formatCurrency(emp.per_day_salary)}
                          </TableCell>
                          <TableCell className="text-center">
                            {!emp.is_finalized ? (
                              <Input
                                type="number"
                                value={emp.lop_days || 0}
                                onChange={(e) => handleBulkLOPChange(emp.employee_id, e.target.value)}
                                className="w-20 text-center mx-auto font-mono"
                                min="0"
                                max="31"
                                step="0.5"
                                data-testid={`bulk-lop-${emp.employee_id}`}
                              />
                            ) : (
                              <span className="font-mono">{emp.lop_days}</span>
                            )}
                          </TableCell>
                          <TableCell className={`text-right font-mono ${emp.lop_deduction > 0 ? 'text-red-600' : ''}`}>
                            {emp.lop_deduction > 0 ? `-${formatCurrency(emp.lop_deduction)}` : '-'}
                          </TableCell>
                          <TableCell className="text-right font-mono font-semibold text-emerald-600">
                            {formatCurrency(emp.final_payable_salary)}
                          </TableCell>
                          <TableCell className="text-center">
                            {emp.is_paid ? (
                              <span className="text-xs text-emerald-600">Paid</span>
                            ) : emp.is_finalized ? (
                              <span className="text-xs text-amber-600">Finalized</span>
                            ) : emp.record_id ? (
                              <span className="text-xs text-blue-600">Saved</span>
                            ) : (
                              <span className="text-xs text-muted-foreground">New</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {bulkOrgId && bulkEmployees.length === 0 && !loadingBulk && (
                <div className="flex items-center justify-center h-40 text-muted-foreground">
                  Click "Load Employees" to fetch employee list
                </div>
              )}
            </div>

            <DialogFooter className="border-t pt-4">
              <Button variant="outline" onClick={() => setBulkEntryDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveBulkEntry} 
                disabled={bulkEmployees.length === 0 || savingBulk}
                data-testid="save-bulk-btn"
              >
                {savingBulk ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 spinner" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save All Records
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Generate Monthly Dialog */}
        <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Generate Monthly Records</DialogTitle>
              <DialogDescription>
                This will create salary records for all active employees for {getMonthName(parseInt(filterMonth))} {filterYear}.
                Existing records will be skipped.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setGenerateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleGenerateMonthly} disabled={generating} data-testid="confirm-generate-btn">
                {generating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 spinner" />
                    Generating...
                  </>
                ) : (
                  'Generate'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirm Finalize Dialog */}
        <AlertDialog open={!!confirmFinalizeId} onOpenChange={() => setConfirmFinalizeId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Finalize Salary Record</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to finalize this record? This will lock the LOP Days and prevent further edits.
                The finalization timestamp will be recorded.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleFinalize(confirmFinalizeId)}
                data-testid="confirm-finalize-btn"
              >
                Finalize
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Confirm Unfinalize Dialog */}
        <AlertDialog open={!!confirmUnfinalizeId} onOpenChange={() => setConfirmUnfinalizeId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-amber-600">Revert Finalization</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <p>Are you sure you want to revert the finalization of this record?</p>
                <p className="font-medium">This will:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Unlock the record for LOP Days editing</li>
                  <li>Restore any advance deductions back to the employee's advance balance</li>
                  <li>Remove the finalization timestamp</li>
                </ul>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleUnfinalize(confirmUnfinalizeId)}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-unfinalize-btn"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Revert Finalization
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Confirm Paid Dialog */}
        <AlertDialog open={!!confirmPaidId} onOpenChange={() => setConfirmPaidId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Mark as Paid</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to mark this record as paid? This action cannot be undone.
                The payment timestamp will be recorded.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleMarkPaid(confirmPaidId)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="confirm-paid-btn"
              >
                <Banknote className="mr-2 h-4 w-4" />
                Mark as Paid
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Confirm Reverse Payment Dialog */}
        <AlertDialog open={!!confirmReversePayId} onOpenChange={() => setConfirmReversePayId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-red-600">Reverse Payment</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <p>Are you sure you want to reverse the payment for this record?</p>
                <p className="font-medium">This will:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Remove the "Paid" status from this record</li>
                  <li>Clear the payment timestamp</li>
                  <li>The record will remain finalized</li>
                </ul>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleReversePayment(confirmReversePayId)}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-reverse-pay-btn"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Reverse Payment
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Confirm Delete Dialog */}
        <AlertDialog open={!!confirmDeleteId} onOpenChange={() => setConfirmDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Salary Record</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this salary record? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="confirm-delete-salary-btn"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
